import { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  where,
  onSnapshot,
  doc,
  Timestamp,
  addDoc,
  serverTimestamp,
  arrayUnion,
  updateDoc,
} from "firebase/firestore";
import {
  ConversationChunk,
  ConversationMessage,
  AIMessage,
} from "../data/types";
import {
  db,
  generateMessageRecommendationsAnthropicFunction,
} from "../data/firebase";
import {
  addMessageToChunkFirestore,
  createChunkFirestore,
  updateChunkMessagesFirestore,
  updateChunkAIChatFirestore,
  updateChunkAIChatSummaryFirestore,
  updateConversationSummaryFirestore,
} from "../data/firestore";
import { useUser } from "../contexts/UserContext";
import {
  analyzeConversation,
  generateCompletion,
  generateMessageRecommendations,
} from "../data/api";
import analytics from "../lib/analytics";
import {
  summarizeAIChatSummary,
  summarizeChunk,
  summarizeCurrentAIChat,
} from "../data/gemini-summarize";
import { isWithinTokenLimit } from "gpt-tokenizer/model/gpt-3.5-turbo";
import * as Sentry from "@sentry/react";
import { runSummaries } from "./summaries";

// Interface for the response type from summarizeAIChat
interface ChunkSummaryResponse {
  summary: string[];
}

// Type for past summaries
type PastSummary = {
  aiChatSummary?: string[];
  conversationMessagesSummary?: string[];
};

// Interface for conversation document data
interface ConversationData {
  summary?: string[];
  createdAt?: Timestamp;
}

// Define the interface for the summary stats
interface SummaryStats {
  tokensBefore: number;
  tokensAfter: number;
}

export const useFetchConversationChunks = (
  conversationId: string,
  targetName: string
) => {
  const [chunks, setChunks] = useState<ConversationChunk[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingFirstTime, setLoadingFirstTime] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [responseLoading, setResponseLoading] = useState(false);
  const [conversationData, setConversationData] =
    useState<ConversationData | null>(null);
  const { userData } = useUser();

  const lastChunk = chunks[chunks.length - 1] || null;

  const [isFirstAIMessage, setIsFirstAIMessage] = useState(true);

  // Listen for changes to the conversation document
  useEffect(() => {
    if (!userData?.uid || !conversationId) return;

    const conversationRef = doc(
      db,
      `users/${userData.uid}/conversations/${conversationId}`
    );

    const unsubscribe = onSnapshot(
      conversationRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data() as ConversationData;
          setConversationData(data);
        } else {
          console.log("No conversation document found");
        }
      },
      (error) => {
        console.error("Error listening to conversation document:", error);
        Sentry.captureException(error, {
          tags: {
            userId: userData?.uid,
            conversationId,
            component: "useFetchConversationChunks",
            operation: "conversation-listener",
          },
        });
        setError(error as Error);
      }
    );

    return () => unsubscribe();
  }, [userData?.uid, conversationId]);

  useEffect(() => {
    const fetchChunks = async () => {
      if (!userData?.uid || !conversationId) return;
      setLoading(true);

      try {
        const chunksRef = collection(
          db,
          `users/${userData.uid}/conversations/${conversationId}/chunks`
        );

        // Ascending + limit keeps the oldest chunks, so anything past the
        // limit silently disappears on reload. Keep this above the longest
        // conversation (210 chunks as of Oct 2026).
        const q = query(chunksRef, orderBy("timestamp", "asc"), limit(500));

        const querySnapshot = await getDocs(q);
        const fetchedChunks = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          // timestamp: doc.data().timestamp?.toDate(),
        })) as ConversationChunk[];

        setChunks(fetchedChunks);
        setError(null);
      } catch (err) {
        console.error("Error fetching chunks", err);
        Sentry.captureException(err, {
          tags: {
            userId: userData?.uid,
            conversationId,
            component: "useFetchConversationChunks",
            operation: "fetchChunks",
          },
        });
        setError(
          err instanceof Error ? err : new Error("Failed to fetch chunks")
        );
      } finally {
        setLoading(false);
        setLoadingFirstTime(false);
      }
    };

    fetchChunks();
  }, [conversationId, userData?.uid]);

  const addNewMessage = async (content: string, sender: "user" | "target") => {
    const message: ConversationMessage = {
      id: crypto.randomUUID(),
      sender,
      content: content.trim(),
    };

    if (lastChunk?.aiChat) {
      await createNewChunkWithMessage(message);
    } else {
      await addMessageToExistingChunk(message);
    }
  };

  const createNewChunkWithMessage = async (message: ConversationMessage) => {
    if (!userData?.uid) throw new Error("No user ID provided");

    // Generate AI chat summary without awaiting
    // summarizeChunk({
    //   targetName,
    //   chunk: lastChunk,
    //   summary: conversationData?.summary || null,
    // })
    //   .then((response: ChunkSummaryResponse | null) => {
    //     if (response && response.summary) {
    //       console.log("Chunk summary generated:", response.summary);

    //       // Update the conversation summary with the newly generated summary
    //       if (userData && response.summary.length > 0) {
    //         const newSummary = [
    //           ...(conversationData?.summary || []),
    //           ...response.summary,
    //           "==end of chunk summary==",
    //         ];
    //         updateConversationSummaryFirestore(
    //           userData,
    //           conversationId,
    //           newSummary
    //         ).catch((error: Error) => {
    //           console.error(
    //             "Failed to save conversation summary to Firestore:",
    //             error
    //           );
    //         });
    //       }
    //     }
    //   })
    //   .catch((error: Error) => {
    //     console.error("Failed to summarize conversation:", error);
    //   });

    const newChunk: ConversationChunk = {
      id: crypto.randomUUID(),
      userId: "test-user",
      targetId: "test-target",
      timestamp: new Date().toISOString(),
      conversationMessages: [message],
      aiChat: null,
    };

    // Update local state first
    setChunks((prev) => [...prev, newChunk]);

    try {
      await createChunkFirestore(userData, conversationId, newChunk);
    } catch (error) {
      // Rollback on error
      setChunks((prev) => prev.filter((chunk) => chunk.id !== newChunk.id));
      console.error(error);
      Sentry.captureException(error, {
        tags: {
          userId: userData?.uid,
          conversationId,
          chunkId: newChunk.id,
          component: "useFetchConversationChunks",
          operation: "createNewChunkWithMessage",
        },
        extra: {
          message,
        },
      });
      throw new Error("Failed to save message to database");
    }
  };

  const addMessageToExistingChunk = async (message: ConversationMessage) => {
    if (!lastChunk || !userData?.uid) throw new Error("No chunk available");

    // Update local state first
    setChunks((prev) => {
      const updated = [...prev];
      const lastIndex = updated.length - 1;
      updated[lastIndex] = {
        ...updated[lastIndex],
        conversationMessages: [
          ...updated[lastIndex].conversationMessages,
          message,
        ],
      };
      return updated;
    });

    try {
      await addMessageToChunkFirestore(
        userData,
        conversationId,
        lastChunk.id,
        message
      );
    } catch (error) {
      // Rollback on error
      setChunks((prev) => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        updated[lastIndex] = lastChunk;
        return updated;
      });
      console.error(error);
      Sentry.captureException(error, {
        tags: {
          userId: userData?.uid,
          conversationId,
          chunkId: lastChunk.id,
          component: "useFetchConversationChunks",
          operation: "addMessageToExistingChunk",
        },
        extra: {
          message,
        },
      });
      throw new Error("Failed to save message to database");
    }
  };

  const editMessage = async (
    chunkId: string,
    messageIndex: number,
    newContent: string,
    isNewMessage: boolean = false
  ) => {
    const chunkToEdit = chunks.find((chunk) => chunk.id === chunkId);
    if (!chunkToEdit || !userData?.uid) return;

    let updatedMessages;
    if (isNewMessage) {
      // Insert new message at the specified index
      updatedMessages = [...chunkToEdit.conversationMessages];
      updatedMessages.splice(messageIndex, 0, {
        id: crypto.randomUUID(),
        sender: updatedMessages[messageIndex - 1].sender, // Use same sender as previous message
        content: newContent,
      });
    } else {
      // Update existing message
      updatedMessages = chunkToEdit.conversationMessages.map((msg, idx) =>
        idx === messageIndex ? { ...msg, content: newContent } : msg
      );
    }

    // Update local state first
    setChunks((prev) =>
      prev.map((chunk) =>
        chunk.id === chunkId
          ? { ...chunk, conversationMessages: updatedMessages }
          : chunk
      )
    );

    try {
      await updateChunkMessagesFirestore(
        userData,
        conversationId,
        chunkId,
        updatedMessages
      );
    } catch (error) {
      // Rollback on error
      setChunks((prev) =>
        prev.map((chunk) => (chunk.id === chunkId ? chunkToEdit : chunk))
      );
      console.error(error);
      Sentry.captureException(error, {
        tags: {
          userId: userData?.uid,
          conversationId,
          chunkId,
          component: "useFetchConversationChunks",
          operation: "editMessage",
        },
        extra: {
          messageIndex,
          newContent,
          isNewMessage,
        },
      });
      throw new Error("Failed to update message in database");
    }
  };

  const deleteMessage = async (chunkId: string, messageIndex: number) => {
    const chunkToEdit = chunks.find((chunk) => chunk.id === chunkId);
    if (!chunkToEdit || !userData?.uid) return;

    const updatedMessages = chunkToEdit.conversationMessages.filter(
      (_, index) => index !== messageIndex
    );

    // Update local state first
    setChunks((prev) =>
      prev.map((chunk) =>
        chunk.id === chunkId
          ? { ...chunk, conversationMessages: updatedMessages }
          : chunk
      )
    );

    try {
      await updateChunkMessagesFirestore(
        userData,
        conversationId,
        chunkId,
        updatedMessages
      );
    } catch (error) {
      // Rollback on error
      setChunks((prev) =>
        prev.map((chunk) => (chunk.id === chunkId ? chunkToEdit : chunk))
      );
      console.error(error);
      Sentry.captureException(error, {
        tags: {
          userId: userData?.uid,
          conversationId,
          chunkId,
          component: "useFetchConversationChunks",
          operation: "deleteMessage",
        },
        extra: {
          messageIndex,
        },
      });
      throw new Error("Failed to delete message from database");
    }
  };

  // const updateChunk = (updatedChunk: ConversationChunk) => {
  //   setChunks((prevChunks) =>
  //     prevChunks.map((chunk) =>
  //       chunk.id === updatedChunk.id ? updatedChunk : chunk
  //     )
  //   );
  // };

  const analyzeChat = async () => {
    if (!userData?.uid || !lastChunk) return;

    setResponseLoading(true);
    try {
      // Create user message requesting analysis
      const userAIMessage: AIMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: "Chat Analysis Requested...",
        type: "analysis-request",
      };

      // Update chunk with user message
      let updatedChunkWithUser = {
        ...lastChunk,
        aiChat: {
          messages: [...(lastChunk?.aiChat?.messages || []), userAIMessage],
        },
      } as ConversationChunk;

      // Update local state and database with user message
      try {
        await updateChunkWithAIChat(updatedChunkWithUser);
      } catch (error) {
        // Check if error is due to document size limit
        if (isDocumentSizeError(error)) {
          console.log(
            "Document size limit exceeded, trimming messages and retrying..."
          );
          // Trim messages from the base chunk first
          const trimmedBase = await trimAIChatMessages(lastChunk, 100);
          // Recreate the updated chunk with trimmed base
          updatedChunkWithUser = {
            ...trimmedBase,
            aiChat: {
              messages: [
                ...(trimmedBase?.aiChat?.messages || []),
                userAIMessage,
              ],
            },
          };
          // Retry the update
          await updateChunkWithAIChat(updatedChunkWithUser);
        } else {
          throw error;
        }
      }

      if (isFirstAIMessage) {
        setIsFirstAIMessage(false);

        try {
          updatedChunkWithUser = await runSummaries(
            updatedChunkWithUser,
            userData?.uid,
            conversationId
          );
          updateChunkWithAIChat(updatedChunkWithUser);
        } catch (error) {
          console.error("Failed to update chunk with summary:", error);
          Sentry.captureException(error, {
            tags: {
              userId: userData?.uid,
              conversationId,
              chunkId: updatedChunkWithUser.id,
              component: "useFetchConversationChunks",
              operation: "analyzeChat-summarize",
            },
          });
          addErrorToAdmin(error);
        }

        // try {
        //   const {
        //     summary: summaryOfNewMessages,
        //     cutoffIndex,
        //     lastSummaryIndex,
        //     didSummarize,
        //     tokensBefore,
        //     tokensAfter,
        //   } = await checkAndSummarizeAIChat(updatedChunkWithUser);

        //   const { didSummarizeSummary, summaryIndex, newSummaryContent } =
        //     await checkAndSummarizeAIChatSummary(updatedChunkWithUser);
        //   console.log("checkAndSummarizeAIChatSummary response", res);

        //   if (didSummarize && typeof cutoffIndex === "number") {
        //     const summaryMessage: AIMessage = {
        //       id: crypto.randomUUID(),
        //       role: "summary",
        //       type: "text",
        //       content: summaryOfNewMessages,
        //       isSummary: true,
        //     };

        //     if (lastSummaryIndex && lastSummaryIndex > 0) {
        //       summaryMessage.content = [
        //         ...updatedChunkWithUser.aiChat.messages[lastSummaryIndex]
        //           .content,
        //         ...summaryOfNewMessages,
        //       ];
        //     }

        //     const filteredMessages = updatedChunkWithUser.aiChat.messages;

        //     // DO NOT FORGET
        //     // //////////////////////////////////////////////////////////////////////////////
        //     // //////////////////////////////////////////////////////////////////////////////
        //     // //////////////////////////////////////////////////////////////////////////////
        //     // //////////////////////////////////////////////////////////////////////////////

        //     // Filter out the previous summary message
        //     // const filteredMessages =
        //     //   updatedChunkWithUser.aiChat.messages.filter(
        //     //     (msg, idx) => idx !== lastSummaryIndex || lastSummaryIndex === 0
        //     //   );

        //     // DO NOT FORGET
        //     // //////////////////////////////////////////////////////////////////////////////
        //     // //////////////////////////////////////////////////////////////////////////////
        //     // //////////////////////////////////////////////////////////////////////////////
        //     // //////////////////////////////////////////////////////////////////////////////

        //     updatedChunkWithUser = {
        //       ...updatedChunkWithUser,
        //       aiChat: {
        //         messages: [
        //           ...filteredMessages.slice(0, cutoffIndex),
        //           summaryMessage,
        //           ...filteredMessages.slice(cutoffIndex),
        //         ],
        //       },
        //     };

        //     updateChunkWithAIChat(updatedChunkWithUser);
        //     addStatToAdmin(tokensBefore as number, tokensAfter as number).catch(
        //       (error) => {
        //         console.error("Failed to add stats to admin:", error);
        //       }
        //     );
        //   }
        // } catch (error) {
        //   console.error("Failed to update chunk with summary:", error);
        //   Sentry.captureException(error, {
        //     tags: {
        //       userId: userData?.uid,
        //       conversationId,
        //       chunkId: updatedChunkWithUser.id,
        //       component: "useFetchConversationChunks",
        //       operation: "analyzeChat-summarize",
        //     },
        //   });
        //   addErrorToAdmin(error);
        // }
      }

      try {
        analytics.track("Analyze Chat");

        const result = await analyzeConversation(chunks, targetName);
        const aiMessage: AIMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: result,
          type: "analyse-chat",
        };

        // Update chunk with AI response
        let updatedChunkWithAI = {
          ...updatedChunkWithUser,
          aiChat: {
            messages: [...updatedChunkWithUser.aiChat.messages, aiMessage],
          },
        };

        try {
          await updateChunkWithAIChat(updatedChunkWithAI);
        } catch (error) {
          // Check if error is due to document size limit
          if (isDocumentSizeError(error)) {
            // Trim messages from the user chunk
            const trimmedUserChunk = await trimAIChatMessages(
              updatedChunkWithUser,
              100
            );
            // Recreate the AI response chunk with trimmed base
            updatedChunkWithAI = {
              ...trimmedUserChunk,
              aiChat: {
                messages: [
                  ...(trimmedUserChunk?.aiChat?.messages || []),
                  aiMessage,
                ],
              },
            };
            // Retry the update
            await updateChunkWithAIChat(updatedChunkWithAI);
          } else {
            throw error;
          }
        }
      } catch (error) {
        // If analysis fails, revert to the state before the user message
        await updateChunkWithAIChat(lastChunk);
        Sentry.captureException(error, {
          tags: {
            userId: userData?.uid,
            conversationId,
            chunkId: lastChunk.id,
            component: "useFetchConversationChunks",
            operation: "analyzeChat-analyze",
          },
        });
        throw error;
      }
    } catch (error) {
      console.error("Failed to analyze chat:", error);
      Sentry.captureException(error, {
        tags: {
          userId: userData?.uid,
          conversationId,
          component: "useFetchConversationChunks",
          operation: "analyzeChat",
        },
      });
      throw error;
    } finally {
      setResponseLoading(false);
    }
  };

  const generateAIResponse = async (userMessage: string) => {
    if (!userData?.uid || !lastChunk) return;

    setResponseLoading(true);
    try {
      // Create user message
      const userAIMessage: AIMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: userMessage,
        type: "text",
      };

      // Update chunk with user message
      let updatedChunkWithUser = {
        ...lastChunk,
        aiChat: {
          messages: [...(lastChunk?.aiChat?.messages || []), userAIMessage],
        },
      } as ConversationChunk;

      // Update local state and database with user message
      try {
        await updateChunkWithAIChat(updatedChunkWithUser);
      } catch (error) {
        // Check if error is due to document size limit
        if (isDocumentSizeError(error)) {
          // Trim messages from the base chunk first
          const trimmedBase = await trimAIChatMessages(lastChunk, 100);
          // Recreate the updated chunk with trimmed base
          updatedChunkWithUser = {
            ...trimmedBase,
            aiChat: {
              messages: [
                ...(trimmedBase?.aiChat?.messages || []),
                userAIMessage,
              ],
            },
          };
          // Retry the update
          await updateChunkWithAIChat(updatedChunkWithUser);
        } else {
          throw error;
        }
      }

      if (isFirstAIMessage) {
        try {
          updatedChunkWithUser = await runSummaries(
            updatedChunkWithUser,
            userData?.uid,
            conversationId
          );
          updateChunkWithAIChat(updatedChunkWithUser);
        } catch (error) {
          console.error("Failed to update chunk with summary:", error);
          Sentry.captureException(error, {
            tags: {
              userId: userData?.uid,
              conversationId,
              chunkId: updatedChunkWithUser.id,
              component: "useFetchConversationChunks",
              operation: "analyzeChat-summarize",
            },
          });
          addErrorToAdmin(error);
        }
        // try {
        //   setIsFirstAIMessage(false);
        //   const {
        //     summary,
        //     cutoffIndex,
        //     lastSummaryIndex,
        //     didSummarize,
        //     tokensBefore,
        //     tokensAfter,
        //   } = await checkAndSummarizeAIChat(updatedChunkWithUser);

        //   if (didSummarize) {
        //     const summaryMessage: AIMessage = {
        //       id: crypto.randomUUID(),
        //       role: "summary",
        //       type: "text",
        //       content: summary,
        //       isSummary: true,
        //     };

        //     if (lastSummaryIndex && lastSummaryIndex > 0) {
        //       summaryMessage.content = [
        //         ...updatedChunkWithUser.aiChat.messages[lastSummaryIndex]
        //           .content,
        //         ...summary,
        //       ];
        //     }

        //     // Filter out the previous summary message
        //     const filteredMessages =
        //       updatedChunkWithUser.aiChat.messages.filter(
        //         (msg, idx) => idx !== lastSummaryIndex || lastSummaryIndex === 0
        //       );

        //     updatedChunkWithUser = {
        //       ...updatedChunkWithUser,
        //       aiChat: {
        //         messages: [
        //           ...filteredMessages.slice(0, cutoffIndex),
        //           summaryMessage,
        //           ...filteredMessages.slice(cutoffIndex),
        //         ],
        //       },
        //     };

        //     updateChunkWithAIChat(updatedChunkWithUser);
        //     addStatToAdmin(tokensBefore as number, tokensAfter as number).catch(
        //       (error) => {
        //         console.error("Failed to add stats to admin:", error);
        //       }
        //     );
        //   }
        // } catch (error) {
        //   console.error("Failed to update chunk with summary:", error);
        //   Sentry.captureException(error, {
        //     tags: {
        //       userId: userData?.uid,
        //       conversationId,
        //       chunkId: updatedChunkWithUser.id,
        //       component: "useFetchConversationChunks",
        //       operation: "generateAIResponse-summarize",
        //     },
        //   });
        //   addErrorToAdmin(error);
        // }
      }

      try {
        analytics.track("Ask Question", {
          question: userMessage,
        });
        const result = await generateCompletion(
          [...chunks.slice(0, -1), updatedChunkWithUser],
          targetName,
          conversationData?.summary || null
        );

        // Create AI message
        const aiMessage: AIMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: result,
          type: "completion",
        };

        // Update chunk with AI response
        let updatedChunkWithAI = {
          ...updatedChunkWithUser,
          aiChat: {
            messages: [...updatedChunkWithUser.aiChat.messages, aiMessage],
          },
        };

        try {
          await updateChunkWithAIChat(updatedChunkWithAI);
        } catch (error) {
          // Check if error is due to document size limit
          if (isDocumentSizeError(error)) {
            // Trim messages from the user chunk
            const trimmedUserChunk = await trimAIChatMessages(
              updatedChunkWithUser,
              100
            );
            // Recreate the AI response chunk with trimmed base
            updatedChunkWithAI = {
              ...trimmedUserChunk,
              aiChat: {
                messages: [
                  ...(trimmedUserChunk?.aiChat?.messages || []),
                  aiMessage,
                ],
              },
            };
            // Retry the update
            await updateChunkWithAIChat(updatedChunkWithAI);
          } else {
            throw error;
          }
        }
      } catch (error) {
        // If AI response fails, revert to the state before the user message
        await updateChunkWithAIChat(lastChunk);
        Sentry.captureException(error, {
          tags: {
            userId: userData?.uid,
            conversationId,
            chunkId: lastChunk.id,
            component: "useFetchConversationChunks",
            operation: "generateAIResponse-completion",
          },
          extra: {
            userMessage,
          },
        });
        throw error;
      }
    } catch (error) {
      console.error("Failed to generate AI response:", error);
      Sentry.captureException(error, {
        tags: {
          userId: userData?.uid,
          conversationId,
          component: "useFetchConversationChunks",
          operation: "generateAIResponse",
        },
        extra: {
          userMessage,
        },
      });
      throw error;
    } finally {
      setResponseLoading(false);
    }
  };

  const updateChunkWithAIChat = async (updatedChunk: ConversationChunk) => {
    if (!userData?.uid) return;

    setChunks((prev) =>
      prev.map((chunk) => (chunk.id === updatedChunk.id ? updatedChunk : chunk))
    );

    try {
      await updateChunkAIChatFirestore(
        userData,
        conversationId,
        updatedChunk.id,
        updatedChunk.aiChat!
      );
    } catch (error) {
      // Rollback on error
      setChunks((prev) =>
        prev.map((chunk) => (chunk.id === updatedChunk.id ? lastChunk! : chunk))
      );
      Sentry.captureException(error, {
        tags: {
          userId: userData?.uid,
          conversationId,
          chunkId: updatedChunk.id,
          component: "useFetchConversationChunks",
          operation: "updateChunkWithAIChat",
        },
      });
      throw error;
    }
  };

  // Helper function to check if error is due to document size limit
  const isDocumentSizeError = (error: any): boolean => {
    return (
      error?.message?.includes("exceeds the maximum allowed size") ||
      error?.code === "invalid-argument"
    );
  };

  // Helper function to trim first N messages from aiChat
  const trimAIChatMessages = async (
    chunk: ConversationChunk,
    count: number = 100
  ): Promise<ConversationChunk> => {
    if (!chunk.aiChat || !chunk.aiChat.messages) {
      return chunk;
    }

    const currentMessages = chunk.aiChat.messages;

    // Remove first 'count' messages
    const trimmedMessages = currentMessages.slice(count);

    const trimmedChunk = {
      ...chunk,
      aiChat: {
        messages: trimmedMessages,
      },
    };

    // Update the chunk in Firestore and local state
    await updateChunkWithAIChat(trimmedChunk);

    return trimmedChunk;
  };

  const addStatToAdmin = async (tokensBefore: number, tokensAfter: number) => {
    try {
      const adminStatsRef = doc(db, "admin/chatSummaryStats");

      // Create the stats object
      const statObject = {
        userId: userData?.uid,
        timestamp: new Date(),
        conversationId,
        tokensBefore,
        tokensAfter,
      };

      // Update the document by pushing the new stat to the log array
      await updateDoc(adminStatsRef, {
        log: arrayUnion(statObject),
      });
    } catch (error) {
      console.error("Failed to save summary stats:", error);
      Sentry.captureException(error, {
        tags: {
          userId: userData?.uid,
          conversationId,
          component: "useFetchConversationChunks",
          operation: "addStatToAdmin",
        },
        extra: {
          tokensBefore,
          tokensAfter,
        },
      });
    }
  };

  const addErrorToAdmin = async (error: any) => {
    try {
      const adminErrorsRef = doc(db, "admin/chatSummaryErrors");

      await updateDoc(adminErrorsRef, {
        log: arrayUnion({
          userId: userData?.uid,
          timestamp: new Date(),
          conversationId,
          error,
        }),
      });
    } catch (error) {
      console.error("Failed to save error:", error);
      Sentry.captureException(error, {
        tags: {
          userId: userData?.uid,
          conversationId,
          component: "useFetchConversationChunks",
          operation: "addErrorToAdmin",
        },
      });
    }
  };

  const addAIMessageToLastChunk = async (message: AIMessage) => {
    if (!lastChunk || !userData?.uid) throw new Error("No chunk available");

    const currentMessages = lastChunk.aiChat?.messages || [];
    const updatedChunk = {
      ...lastChunk,
      aiChat: {
        messages: [...currentMessages, message],
      },
    };

    await updateChunkWithAIChat(updatedChunk);
  };

  const generateRecommendations = async () => {
    if (!userData?.uid || !lastChunk) return;

    setResponseLoading(true);
    try {
      analytics.track("Get Replies");
      // Create user message indicating recommendation request
      const userAIMessage: AIMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: "Get Replies Requested...",
        type: "replies-request",
      };

      // Update chunk with user message
      let updatedChunkWithUser = {
        ...lastChunk,
        aiChat: {
          messages: [...(lastChunk?.aiChat?.messages || []), userAIMessage],
        },
      } as ConversationChunk;

      // Update local state and database with user message
      try {
        await updateChunkWithAIChat(updatedChunkWithUser);
      } catch (error) {
        // Check if error is due to document size limit
        if (isDocumentSizeError(error)) {
          // Trim messages from the base chunk first
          const trimmedBase = await trimAIChatMessages(lastChunk, 100);
          // Recreate the updated chunk with trimmed base
          updatedChunkWithUser = {
            ...trimmedBase,
            aiChat: {
              messages: [
                ...(trimmedBase?.aiChat?.messages || []),
                userAIMessage,
              ],
            },
          };
          // Retry the update
          await updateChunkWithAIChat(updatedChunkWithUser);
        } else {
          throw error;
        }
      }

      if (isFirstAIMessage) {
        setIsFirstAIMessage(false);

        try {
          updatedChunkWithUser = await runSummaries(
            updatedChunkWithUser,
            userData?.uid,
            conversationId
          );
          updateChunkWithAIChat(updatedChunkWithUser);
        } catch (error) {
          console.error("Failed to update chunk with summary:", error);
          Sentry.captureException(error, {
            tags: {
              userId: userData?.uid,
              conversationId,
              chunkId: updatedChunkWithUser.id,
              component: "useFetchConversationChunks",
              operation: "analyzeChat-summarize",
            },
          });
          addErrorToAdmin(error);
        }

        // try {
        //   const {
        //     summary,
        //     cutoffIndex,
        //     lastSummaryIndex,
        //     didSummarize,
        //     tokensBefore,
        //     tokensAfter,
        //   } = await checkAndSummarizeAIChat(updatedChunkWithUser);

        //   if (didSummarize) {
        //     const summaryMessage: AIMessage = {
        //       id: crypto.randomUUID(),
        //       role: "summary",
        //       type: "text",
        //       content: summary,
        //       isSummary: true,
        //     };

        //     if (lastSummaryIndex && lastSummaryIndex > 0) {
        //       summaryMessage.content = [
        //         ...updatedChunkWithUser.aiChat.messages[lastSummaryIndex]
        //           .content,
        //         ...summary,
        //       ];
        //     }

        //     // Filter out the previous summary message
        //     const filteredMessages =
        //       updatedChunkWithUser.aiChat.messages.filter(
        //         (msg, idx) => idx !== lastSummaryIndex || lastSummaryIndex === 0
        //       );

        //     updatedChunkWithUser = {
        //       ...updatedChunkWithUser,
        //       aiChat: {
        //         messages: [
        //           ...filteredMessages.slice(0, cutoffIndex),
        //           summaryMessage,
        //           ...filteredMessages.slice(cutoffIndex),
        //         ],
        //       },
        //     };

        //     updateChunkWithAIChat(updatedChunkWithUser);
        //     addStatToAdmin(tokensBefore as number, tokensAfter as number).catch(
        //       (error) => {
        //         console.error("Failed to add stats to admin:", error);
        //       }
        //     );
        //   }
        // } catch (error) {
        //   console.error("Failed to update chunk with summary:", error);
        //   Sentry.captureException(error, {
        //     tags: {
        //       userId: userData?.uid,
        //       conversationId,
        //       chunkId: updatedChunkWithUser.id,
        //       component: "useFetchConversationChunks",
        //       operation: "generateRecommendations-summarize",
        //     },
        //   });
        //   addErrorToAdmin(error);
        // }
      }

      try {
        // Get recommendations
        const result = await generateMessageRecommendations(chunks, targetName);
        const aiMessage: AIMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: result,
          type: "generate-messages",
        };

        // Update chunk with AI response
        let updatedChunkWithAI = {
          ...updatedChunkWithUser,
          aiChat: {
            messages: [...updatedChunkWithUser.aiChat.messages, aiMessage],
          },
        };

        try {
          await updateChunkWithAIChat(updatedChunkWithAI);
        } catch (error) {
          // Check if error is due to document size limit
          if (isDocumentSizeError(error)) {
            // Trim messages from the user chunk
            const trimmedUserChunk = await trimAIChatMessages(
              updatedChunkWithUser,
              100
            );
            // Recreate the AI response chunk with trimmed base
            updatedChunkWithAI = {
              ...trimmedUserChunk,
              aiChat: {
                messages: [
                  ...(trimmedUserChunk?.aiChat?.messages || []),
                  aiMessage,
                ],
              },
            };
            // Retry the update
            await updateChunkWithAIChat(updatedChunkWithAI);
          } else {
            throw error;
          }
        }
      } catch (error) {
        // If recommendation generation fails, revert to the state before the user message
        await updateChunkWithAIChat(lastChunk);
        Sentry.captureException(error, {
          tags: {
            userId: userData?.uid,
            conversationId,
            chunkId: lastChunk.id,
            component: "useFetchConversationChunks",
            operation: "generateRecommendations-generate",
          },
        });
        throw error;
      }
    } catch (error) {
      console.error("Failed to generate recommendations:", error);
      Sentry.captureException(error, {
        tags: {
          userId: userData?.uid,
          conversationId,
          component: "useFetchConversationChunks",
          operation: "generateRecommendations",
        },
      });
      throw error;
    } finally {
      setResponseLoading(false);
    }
  };

  const processExtractedMessages = async (
    messages: Array<{ content: string; side: string }>
  ) => {
    if (!userData?.uid) throw new Error("No chunk available");

    const processedMessages: ConversationMessage[] = messages.map((msg) => ({
      id: crypto.randomUUID(),
      content: msg.content,
      sender: msg.side === "left" ? "target" : "user",
    }));

    // If last chunk has AI messages, create a new chunk
    if (lastChunk?.aiChat) {
      // Generate AI chat summary without awaiting
      // summarizeChunk({
      //   targetName,
      //   chunk: lastChunk,
      //   summary: conversationData?.summary || null,
      // })
      //   .then((response: ChunkSummaryResponse | null) => {
      //     if (response && response.summary) {
      //       console.log("Chunk summary generated:", response.summary);

      //       // Update the conversation summary with the newly generated summary
      //       if (userData && response.summary.length > 0) {
      //         const newSummary = [
      //           ...(conversationData?.summary || []),
      //           ...response.summary,
      //           "==end of chunk summary==",
      //         ];
      //         updateConversationSummaryFirestore(
      //           userData,
      //           conversationId,
      //           newSummary
      //         ).catch((error: Error) => {
      //           console.error(
      //             "Failed to save conversation summary to Firestore:",
      //             error
      //           );
      //         });
      //       }
      //     }
      //   })
      //   .catch((error: Error) => {
      //     console.error("Failed to summarize conversation:", error);
      //   });

      const newChunk: ConversationChunk = {
        id: crypto.randomUUID(),
        userId: "test-user",
        targetId: "test-target",
        timestamp: new Date().toISOString(),
        conversationMessages: processedMessages,
        aiChat: null,
      };

      // Update local state first
      setChunks((prev) => [...prev, newChunk]);

      try {
        await createChunkFirestore(userData, conversationId, newChunk);
      } catch (error) {
        // Rollback on error
        setChunks((prev) => prev.filter((chunk) => chunk.id !== newChunk.id));
        console.error(error);
        Sentry.captureException(error, {
          tags: {
            userId: userData?.uid,
            conversationId,
            chunkId: newChunk.id,
            component: "useFetchConversationChunks",
            operation: "processExtractedMessages-create",
          },
          extra: {
            messagesCount: messages.length,
          },
        });
        throw new Error("Failed to save extracted messages to database");
      }
    } else {
      // Append to existing chunk if no AI messages
      if (!lastChunk) throw new Error("No chunk available");

      // Update local state first
      setChunks((prev) => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        updated[lastIndex] = {
          ...updated[lastIndex],
          conversationMessages: [
            ...updated[lastIndex].conversationMessages,
            ...processedMessages,
          ],
        };
        return updated;
      });

      try {
        await updateChunkMessagesFirestore(
          userData,
          conversationId,
          lastChunk.id,
          [...lastChunk.conversationMessages, ...processedMessages]
        );
      } catch (error) {
        // Rollback on error
        setChunks((prev) => {
          const updated = [...prev];
          const lastIndex = updated.length - 1;
          updated[lastIndex] = lastChunk;
          return updated;
        });
        console.error(error);
        Sentry.captureException(error, {
          tags: {
            userId: userData?.uid,
            conversationId,
            chunkId: lastChunk.id,
            component: "useFetchConversationChunks",
            operation: "processExtractedMessages-update",
          },
          extra: {
            messagesCount: messages.length,
          },
        });
        throw new Error("Failed to save extracted messages to database");
      }
    }
  };

  const deleteAIChat = async () => {
    if (!userData?.uid || !lastChunk) return;

    const updatedChunk = {
      ...lastChunk,
      aiChat: null,
    };

    try {
      setChunks((prev) =>
        prev.map((chunk) => (chunk.id === lastChunk.id ? updatedChunk : chunk))
      );

      await updateChunkAIChatFirestore(
        userData,
        conversationId,
        lastChunk.id,
        null
      );
    } catch (error) {
      // Rollback on error
      setChunks((prev) =>
        prev.map((chunk) => (chunk.id === lastChunk.id ? lastChunk : chunk))
      );
      console.error("Failed to delete AI chat:", error);
      Sentry.captureException(error, {
        tags: {
          userId: userData?.uid,
          conversationId,
          chunkId: lastChunk.id,
          component: "useFetchConversationChunks",
          operation: "deleteAIChat",
        },
      });
      throw error;
    }
  };

  const moveMessage = async (chunkId: string, messageIndex: number) => {
    const chunkToEdit = chunks.find((chunk) => chunk.id === chunkId);
    if (!chunkToEdit || !userData?.uid) return;

    const messageToMove = chunkToEdit.conversationMessages[messageIndex];
    const updatedMessages = [...chunkToEdit.conversationMessages];

    // Toggle the sender between 'user' and 'target'
    updatedMessages[messageIndex] = {
      ...messageToMove,
      sender: messageToMove.sender === "user" ? "target" : "user",
    };

    // Update local state first
    setChunks((prev) =>
      prev.map((chunk) =>
        chunk.id === chunkId
          ? { ...chunk, conversationMessages: updatedMessages }
          : chunk
      )
    );

    try {
      await updateChunkMessagesFirestore(
        userData,
        conversationId,
        chunkId,
        updatedMessages
      );
    } catch (error) {
      // Rollback on error
      setChunks((prev) =>
        prev.map((chunk) => (chunk.id === chunkId ? chunkToEdit : chunk))
      );
      console.error(error);
      Sentry.captureException(error, {
        tags: {
          userId: userData?.uid,
          conversationId,
          chunkId,
          component: "useFetchConversationChunks",
          operation: "moveMessage",
        },
        extra: {
          messageIndex,
        },
      });
      throw new Error("Failed to move message in database");
    }
  };

  return {
    chunks,
    loading,
    loadingFirstTime,
    error,
    lastChunk,
    responseLoading,
    conversationData,
    addNewMessage,
    editMessage,
    deleteMessage,
    analyzeChat,
    generateAIResponse,
    generateRecommendations,
    processExtractedMessages,
    deleteAIChat,
    moveMessage,
  };
};
